// 💬 Theme bootstrap — runs once on the client before the Vue app
// mounts. Reads `localStorage.theme` and `localStorage.lightTheme`
// and stamps the right class onto <html> so the very first paint
// already has the correct background/colors.
//
// Why a plugin instead of relying on the composable's auto-init:
// the composable only runs when `useTheme()` is called from a
// component, which happens *after* the layout starts rendering. By
// then the user has already seen a flash of default-theme paint.
// This plugin closes that gap.
//
// Keep this in sync with the resolution rules in
// app/composables/useTheme.ts (single source of truth for keys,
// variant ids, and defaults).

const VALID_VARIANTS = new Set([
  'pearl', 'aurora', 'glacier', 'twilight', 'sage', 'latte',
])
const DEFAULT_VARIANT = 'pearl'

export default defineNuxtPlugin(() => {
  if (typeof document === 'undefined') return

  try {
    const html = document.documentElement
    const mode = localStorage.theme === 'light' ? 'light' : 'dark' // dark-first default
    const stored = localStorage.lightTheme as string | undefined
    const variant = stored && VALID_VARIANTS.has(stored) ? stored : DEFAULT_VARIANT

    // Strip any prior light-* class (e.g. set by a previous nav).
    for (const v of VALID_VARIANTS) html.classList.remove(`light-${v}`)

    if (mode === 'dark') {
      html.classList.add('dark')
    } else {
      html.classList.remove('dark')
      html.classList.add(`light-${variant}`)
    }
  } catch {
    // localStorage can throw in private browsing on some platforms.
    // Falling back to default (dark) is fine — useTheme() will
    // re-run init shortly anyway.
  }
})
